<template>
    <display-text-field v-if="field.fieldType === 'DISPLAY_TEXT'" :field="field" />

    <div v-else-if="field.options?.approvalField" class="q-mb-md" style="max-width: 600px; margin-left: auto; margin-right: auto; text-align: left;">
        <FieldHeader :field="field" class="q-mb-xs" />
        <div class="text-body1" aria-live="polite">{{ approvedByName || '(Not yet approved)' }}</div>
    </div>

    <div v-else class="q-mb-md">

        <!-- Client opted out toggle / indicator -->
        <div style="max-width: 600px; margin: 0 auto 6px; text-align: left; color: grey; font-size: 0.8em;">
            <q-checkbox
                v-if="canOptOut && !readOnly"
                dense
                size="sm"
                color="grey"
                :model-value="isOptedOut"
                label="Client opted out"
                @update:model-value="onOptedOutChange"
            />
            <q-badge
            v-else-if="canOptOut && isOptedOut"
                color="grey-6"
                label="Client opted out"
            />
        </div>

        <!-- Dynamic field component (visually disabled when opted out) -->
        <div :style="isOptedOut ? 'opacity: 0.4; pointer-events: none;' : ''">
            <component
                :is="resolvedFieldComponent"
                v-if="resolvedFieldComponent"
                :field="field"
                :model-value="getValue()"
                :text-input-value="getValue('text_input')" 
                :read-only="readOnly || field.options?.readOnly"
                :options="selectOptions"
                :rules="dateValidationRules"
                :date-options="datePickerOptions"
                @update:model-value="onFieldUpdate"
                @update:text-input-value="setValue($event, 'text_input')"
                @blur="onFieldBlur"
            />

            <!-- Optional comments field stored in separate 'comments' key -->
            <q-input
                v-if="field.options?.comments"
                filled
                autogrow
                type="textarea"
                dense
                style="max-width: 600px; margin: 8px auto 0;"
                :placeholder="field.options?.commentsPlaceholder || 'Comments...'"
                :model-value="getValue('comments')"
                :readonly="readOnly || field.options?.readOnly"
                @update:model-value="setValue($event, 'comments'); onFieldBlur()"
            />
        </div>
    </div>
</template>

<script>
    import FieldHeader from './DynamicFields/FieldHeader.vue';
    import DurationField from './DynamicFields/DurationField.vue';
    import StaffNameField from './DynamicFields/StaffNameField.vue';
    import ChecklistField from './DynamicFields/ChecklistField.vue';
    import DateField from './DynamicFields/DateField.vue';
    import CellsField from './DynamicFields/CellsField.vue';
    import DisplayTextField from './DynamicFields/DisplayTextField.vue';
    import MultiField from './DynamicFields/MultiField.vue';
    import RadioField from './DynamicFields/RadioField.vue';
    import ScaleField from './DynamicFields/ScaleField.vue';
    import SelectField from './DynamicFields/SelectField.vue';
    import StarRatingField from './DynamicFields/StarRatingField.vue';
    import TextField from './DynamicFields/TextField.vue';
    import selectOptionsMixin from './mixins/selectOptions.mixin';
    import fieldTypesAndDateMixin from './mixins/fieldTypesAndDate.mixin';

    export default {
        name: "DynamicFormField",
        mixins: [selectOptionsMixin, fieldTypesAndDateMixin],
        components: {
            FieldHeader,
            DurationField,
            StaffNameField,
            ChecklistField,
            DateField,
            CellsField,
            DisplayTextField,
            MultiField,
            RadioField,
            ScaleField,
            SelectField,
            StarRatingField,
            TextField,
        },
        props: {
            field: {
                type: Object,
                required: true,
            },
            response: {
                type: Object,
                default: () => ({}),
            },
            approvedByName: {
                type: String,
                default: null,
            },
            readOnly: {
                type: Boolean,
                default: false,
            },
            showOptOut: {
                type: Boolean,
                default: true,
            },
        },
        emits: ['field-value'],
        data() {
            return {
                fieldError: null,
            };
        },
        methods: {
            /**
             * Restricts available dates in the date picker based on field's options
             * @param date date string value to check against
             */
            datePickerOptions(date) {
                return this.buildDatePickerOptions(this.field.options)(date);
            },
            /**
             * Return the current field's value from the full response data object.
             * Response data is [field_id]: { value: any, ... } by default.
             * Some fields might need more than one keyed value. Supply an alternate key to fetch.
             * @param key optional key to retrieve specific value
             */
            getValue(key='value') {
                const fieldData = this.response[this.field.id];
                const fieldType = this.field.fieldType;
                const baseType = this.getBaseFieldType(fieldType);
                if (!fieldData) {
                    if (fieldType === 'SELECT' && this.field.options?.multiple && key === 'value') {
                        return [];
                    }
                    if (['STAR_RATING'].includes(fieldType) && key !== 'comments') {
                        //By default, STAR_RATING should return 0 if no value set
                        this.$emit('field-value', this.field.id, key, 0);
                        return 0;
                    }
                    return "";
                }
                if (baseType === 'DATE' && key === 'value') {
                    const hasTime = fieldType === 'DATETIME';
                    // GET value is 'dd/mm/yyyy[ hh:mm]' but saved may be ISO or raw string
                    const storedValue = fieldData[key];
                    if (!storedValue) return "";

                    // If it's already in the display format, return as-is
                    const displayPattern = hasTime
                        ? /^\d{1,2}\/\d{1,2}\/\d{4} \d{1,2}:\d{2}$/
                        : /^\d{1,2}\/\d{1,2}\/\d{4}$/;
                    if (typeof storedValue === 'string' && displayPattern.test(storedValue)) {
                        return storedValue;
                    }

                    // If it's a non-ISO string (partial/invalid user input like "12/3"), don't let JS guess.
                    if (typeof storedValue === 'string') {
                        const looksIso = /^\d{4}-\d{2}-\d{2}(?:[T ].*)?$/.test(storedValue);
                        if (!looksIso) {
                            return storedValue;
                        }
                    }

                    // Otherwise try to parse as a Date (e.g. ISO string or Date object)
                    const dateObj = storedValue instanceof Date ? storedValue : new Date(storedValue);
                    if (isNaN(dateObj.getTime())) {
                        // If parsing fails (e.g. "69/21/4234"), fall back to the raw string
                        return typeof storedValue === 'string' ? storedValue : "";
                    }

                    const day = String(dateObj.getDate()).padStart(2, '0');
                    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                    const year = dateObj.getFullYear();
                    if (hasTime) {
                        const hours = String(dateObj.getHours()).padStart(2, '0');
                        const minutes = String(dateObj.getMinutes()).padStart(2, '0');
                        return `${day}/${month}/${year} ${hours}:${minutes}`;
                    }
                    return `${day}/${month}/${year}`;
                }

                return fieldData[key];
            },
            /**
             * Emit call to set the current field's value in the stored response data object.
             * Some fields might need to set a distinct keyed value. Supply an alternate key to set.
             * @param value value to set for this field
             * @param key optional key to set specific value
             */
            setValue(value, key='value') {
                const baseType = this.getBaseFieldType(this.field.fieldType);
                if (baseType === 'DATE' && key === 'value') {
                    const hasTime = this.field.fieldType === 'DATETIME';
                    // SET value is 'dd/mm/yyyy[ hh:mm]' but we want to store as ISO string if valid
                    if (typeof value === 'string') {
                        // Only attempt conversion when the user has a full, well-formed value.
                        // Partial inputs like "12/3" should be stored as-is.
                        const fullPattern = hasTime
                            ? /^(\d{1,2})\/(\d{1,2})\/(\d{4}) (\d{1,2}):(\d{2})$/
                            : /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
                        const match = value.match(fullPattern);
                        if (match) {
                            const day = parseInt(match[1], 10);
                            const month = parseInt(match[2], 10);
                            const year = parseInt(match[3], 10);
                            const hour = hasTime ? parseInt(match[4], 10) : 0;
                            const minute = hasTime ? parseInt(match[5], 10) : 0;

                            const isValidDateTime = (() => {
                                if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) return false;
                                if (month < 1 || month > 12) return false;
                                if (day < 1) return false;
                                const daysInMonth = new Date(year, month, 0).getDate();
                                if (day > daysInMonth) return false;
                                if (hasTime && (hour < 0 || hour > 23 || minute < 0 || minute > 59)) return false;
                                return true;
                            })();

                            if (isValidDateTime) {
                                if (hasTime) {
                                    // Store wall-clock time as a local Date; round-trips back to the
                                    // same DD/MM/YYYY HH:mm via getHours/getMinutes on read.
                                    value = new Date(year, month - 1, day, hour, minute).toISOString();
                                } else {
                                    // Use UTC to avoid timezone shifting the date when serialized
                                    value = new Date(Date.UTC(year, month - 1, day)).toISOString();
                                }
                            }
                        }
                    }
                }
                this.$emit('field-value', this.field.id, key, value);
            },
            onFieldUpdate(value) {
                this.setValue(value);
            },
            /**
             * Toggle the opted-out state for this field.
             * Emits a field-value event with key 'opted_out'.
             */
            onOptedOutChange(val) {
                this.$emit('field-value', this.field.id, 'opted_out', val);
                this.$nextTick(() => this.onFieldBlur());
            },
            /**
             * Support function to call on clickaway events; essentially a custom lazy-validation trigger function
             */
            onFieldBlur() {
                this.validateField();
            },
            /**
             * Custom function to validate the current field without checking for required state.
             * Returns true if empty or valid, false otherwise. Sets fieldError to the error message string if invalid.
             */
            validateField() {
                if (this.field.options?.approvalField) return true;
                // Opted-out fields are always considered valid
                if (this.isOptedOut) return true;

                const value = this.getValue('value');
                const fieldType = this.field.fieldType;
                const baseType = this.getBaseFieldType(fieldType);

                this.fieldError = null;
                
                // Required fields are enforced at parent level
                if (value === undefined || value === null || value === '' || value === 0) {
                    return true;
                }
                
                if (fieldType === 'DURATION') {
                    const units = this.field.options?.units || ['minute', 'hour', 'day', 'week', 'month', 'year'];
                    if (!Number.isFinite(Number(value.amount)) || Number(value.amount) <= 0 || !units.includes(value.unit)) {
                        this.fieldError = 'requires a positive amount and an allowed unit.';
                        return false;
                    }
                }
                // Check text isn't whitespace
                if (fieldType === 'TEXT' || fieldType === 'TEXT_LINE' || fieldType === 'TEXT_AREA') {
                    if (typeof value === 'string' && value.trim() === '') {
                        this.fieldError = `Field "${this.field.label}" cannot be empty.`;
                        return false;
                    }
                }

                // Validate date fields with Quasar rules
                if (baseType === 'DATE') {
                    for (const rule of this.dateValidationRules) {
                        const result = rule(value);
                        if (result !== true) {
                            this.fieldError = `Field "${this.field.label}": ${result}`;
                            return false;
                        }
                    }
                }

                return true;
            },
        },
        computed: {
            canOptOut() {
                return this.showOptOut && this.field.allowOptOut !== false;
            },
            isOptedOut() {
                return this.response?.[this.field.id]?.opted_out === true;
            },
            resolvedFieldComponent() {
                return this.resolveFieldComponentByType(this.field.fieldType, { includeMulti: true });
            },
            /**
             * Options for SELECT-type fields.
             * Populates depending on specified options.
             * If options is string in enum [ 'truefalse', 'yesno', 'oyesno', 'likert' ] uses a predefined array
             * If options is array of primitives, converts to array of { value, label } objects
             * Otherwise, expects and treats as array of { value, label } objects
             * @return Array of option objects with 'value' and 'label' keys
             */
            selectOptions() {
                return this.buildSelectOptions(this.field.options?.options || []);
            },
            /**
             * Rules for passing to Quasar for validating an input date field string.
             * String is in form "DD/MM/YYYY".
             * @return Array with validation function for Quasar
             */
            dateValidationRules() {
                return this.buildDateValidationRules(this.field.options || {}, {
                    hasTime: this.field.fieldType === 'DATETIME',
                });
            },
        },
    };
</script>

<style scoped>
    .dynamic-form-field {
        margin-bottom: 1rem;
    }
</style>
